import { createContext, createElement, lazy, useContext } from "react";
import { createRoutesFromChildren, matchRoutes } from "react-router-dom";

const RETRY_DELAY_MS = 800;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

let retryEpoch = 0;

/** Lets screens whose download failed fetch again on their next render (called by error boundaries). */
export const allowFailedScreensToRetry = () => {
  retryEpoch += 1;
};

/**
 * React.lazy with a `preload()` method. A failed download is retried once. After a
 * final failure the error reaches the nearest boundary; once the user retries, a
 * fresh lazy instance downloads the screen again instead of rethrowing the cached
 * rejection.
 */
export function lazyWithPreload(factory) {
  let promise = null;
  let failed = false;
  let lazyEpoch = retryEpoch;
  const load = () => {
    if (!promise) {
      promise = factory()
        .catch(() => wait(RETRY_DELAY_MS).then(factory))
        .catch((error) => {
          promise = null;
          failed = true;
          throw error;
        });
    }
    return promise;
  };
  let LazyScreen = lazy(load);
  const PreloadableScreen = (props) => {
    if (failed && lazyEpoch !== retryEpoch) {
      failed = false;
      lazyEpoch = retryEpoch;
      LazyScreen = lazy(load);
    }
    return createElement(LazyScreen, props);
  };
  PreloadableScreen.preload = () => load().catch(() => undefined);
  return PreloadableScreen;
}

const preloadElement = (element) => {
  if (element?.type?.preload) element.type.preload();
};

/** Starts downloading every lazy screen that would render for `path`. */
export function preloadMatchingRoutes(routeChildren, path) {
  if (!routeChildren || !path) return;
  const matches = matchRoutes(createRoutesFromChildren(routeChildren), path) || [];
  matches.forEach(({ route }) => preloadElement(route.element));
}

const noop = () => {};
export const RoutePreloadContext = createContext(noop);
export const useRoutePreload = () => useContext(RoutePreloadContext);

const isConstrainedNetwork = () => {
  const connection = typeof navigator !== "undefined" ? navigator.connection : null;
  return Boolean(connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || ""));
};

/** Preloads paths one by one while the main thread is idle; returns a cancel function. */
export function preloadWhenIdle(paths, preload) {
  if (!paths.length || isConstrainedNetwork()) return noop;
  const queue = [...new Set(paths)];
  let handle = null;
  let cancelled = false;
  const hasIdleCallback = typeof window.requestIdleCallback === "function";
  const schedule = () => {
    handle = hasIdleCallback
      ? window.requestIdleCallback(next, { timeout: 4000 })
      : window.setTimeout(next, 1500);
  };
  const next = () => {
    if (cancelled || !queue.length) return;
    preload(queue.shift());
    if (queue.length) schedule();
  };
  schedule();
  return () => {
    cancelled = true;
    if (hasIdleCallback) window.cancelIdleCallback(handle);
    else window.clearTimeout(handle);
  };
}
